/*
  HeliBaltic — Sales landing page shared JS (hb-slp.js)
  Loaded (deferred) only by layout/hb-sales-landing.liquid.
  Handles: access gate, POGA dialogs, scroll reveal, photo lightbox,
  sticky mobile CTA, form success focus. Event delegation where possible
  so the hb-slp-* sections stay markup-only and editor-safe.
  2026-07-13
*/
(function () {
  'use strict';

  var STORE_KEY = 'hbSlpAuth';

  function sha256Hex(str) {
    var data = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', data).then(function (buf) {
      return Array.prototype.map
        .call(new Uint8Array(buf), function (b) {
          return ('0' + b.toString(16)).slice(-2);
        })
        .join('');
    });
  }

  /* ---------- access gate ---------- */
  function initGate() {
    var gate = document.querySelector('[data-hb-gate]');
    if (!gate || gate.dataset.hbInit === '1') return;
    gate.dataset.hbInit = '1';

    var expected = gate.dataset.gateHash || '';

    function unlock() {
      try {
        sessionStorage.setItem(STORE_KEY, expected);
      } catch (e) { /* private mode */ }
      gate.remove();
      document.documentElement.style.overflow = '';
    }

    // Never block the theme editor preview.
    if (window.Shopify && Shopify.designMode) {
      gate.remove();
      return;
    }
    if (!expected) {
      gate.remove();
      return;
    }
    if (!window.crypto || !window.crypto.subtle) {
      unlock(); // very old browser / non-HTTPS: fail open, don't brick the offer
      return;
    }

    var stored = null;
    try {
      stored = sessionStorage.getItem(STORE_KEY);
    } catch (e) { /* private mode */ }

    var urlKey = new URLSearchParams(window.location.search).get('k');

    if (stored === expected) {
      unlock();
    } else if (urlKey) {
      sha256Hex(urlKey.trim().toLowerCase()).then(function (h) {
        if (h === expected) {
          unlock();
          var url = new URL(window.location.href);
          url.searchParams.delete('k');
          history.replaceState(null, '', url.toString());
        } else {
          showGate();
        }
      });
    } else {
      showGate();
    }

    function showGate() {
      gate.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      var form = gate.querySelector('[data-hb-gate-form]');
      var input = gate.querySelector('.hb-slp__gate-input');
      var error = gate.querySelector('[data-hb-gate-error]');
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        sha256Hex(input.value.trim().toLowerCase()).then(function (h) {
          if (h === expected) {
            unlock();
          } else {
            error.textContent = error.dataset.msg || 'Incorrect code.';
            gate.setAttribute('data-shake', '');
            setTimeout(function () {
              gate.removeAttribute('data-shake');
            }, 400);
            input.select();
          }
        });
      });
      input.focus();
    }
  }

  /* ---------- dialogs + lightbox (delegated) ---------- */
  function initDelegation() {
    if (document.documentElement.dataset.hbSlpDelegated === '1') return;
    document.documentElement.dataset.hbSlpDelegated = '1';

    document.addEventListener('click', function (e) {
      var t = e.target;

      var opener = t.closest && t.closest('[data-hb-dialog]');
      if (opener) {
        var dlg = document.querySelector('[data-hb-dialog-id="' + opener.dataset.hbDialog + '"]');
        if (dlg && typeof dlg.showModal === 'function') dlg.showModal();
        return;
      }

      var closer = t.closest && t.closest('[data-hb-dialog-close]');
      if (closer) {
        var d = closer.closest('dialog');
        if (d) d.close();
        return;
      }

      var lbTile = t.closest && t.closest('[data-hb-lb-src]');
      if (lbTile) {
        var lb = document.querySelector('[data-hb-slp-lightbox]');
        if (lb) {
          lb.querySelector('img').src = lbTile.dataset.hbLbSrc;
          if (typeof lb.showModal === 'function') lb.showModal();
        }
        return;
      }

      // click on the backdrop area of any hb-slp dialog closes it
      if (t instanceof HTMLDialogElement && t.open && t.className.indexOf('hb-slp__') === 0) {
        t.close();
      }
    });

    document.addEventListener('close', function (e) {
      var lb = e.target;
      if (lb && lb.matches && lb.matches('[data-hb-slp-lightbox]')) {
        lb.querySelector('img').src = '';
      }
    }, true);
  }

  /* ---------- scroll reveal ---------- */
  function initReveal() {
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var els = document.querySelectorAll('[data-hb-reveal]:not([data-hb-rv="1"])');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.dataset.hbRv = '1';
        el.classList.add('is-in');
      });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    els.forEach(function (el) {
      el.dataset.hbRv = '1';
      io.observe(el);
    });
  }

  /* ---------- sticky mobile CTA ---------- */
  function initSticky() {
    var sticky = document.querySelector('[data-hb-sticky]');
    if (!sticky || sticky.dataset.hbInit === '1') return;
    sticky.dataset.hbInit = '1';
    var hero = document.querySelector('[data-hb-hero-watch]');
    var formBlock = document.getElementById('hb-slp-form');
    if (!hero || !formBlock || !('IntersectionObserver' in window)) return;
    var heroVisible = true;
    var formVisible = false;
    var sync = function () {
      if (heroVisible || formVisible) {
        sticky.setAttribute('data-hidden', '');
      } else {
        sticky.removeAttribute('data-hidden');
      }
    };
    new IntersectionObserver(function (entries) {
      heroVisible = entries[0].isIntersecting;
      sync();
    }).observe(hero);
    new IntersectionObserver(function (entries) {
      formVisible = entries[0].isIntersecting;
      sync();
    }, { threshold: 0.15 }).observe(formBlock);
  }

  /* ---------- form success focus ---------- */
  function initFormSuccess() {
    var success = document.querySelector('[data-hb-form-success]');
    if (success && success.dataset.hbInit !== '1') {
      success.dataset.hbInit = '1';
      var block = document.getElementById('hb-slp-form');
      if (block) block.scrollIntoView({ block: 'center' });
      success.focus({ preventScroll: true });
    }
  }

  function initAll() {
    initGate();
    initDelegation();
    initReveal();
    initSticky();
    initFormSuccess();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', initAll);
})();
